import { Portal, Root as ModalRoot } from '@radix-ui/react-dialog';
import { cloneElement, useCallback, useRef } from 'react';

import {
  DEFAULT_PANEL_WIDTH,
  OVERLAY_CLASSES,
  PANEL_CLASSES,
} from './constants';
import { SlideOutOverlay } from './SlideOutOverlay';
import { SlideOutPanel } from './SlideOutPanel';
import type { SlideOutProps } from './types';

export function SlideOut({
  children,
  panelWidth = DEFAULT_PANEL_WIDTH,
  panelClassName,
  ...delegatedProps
}: SlideOutProps) {
  const overlayRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);

  const closeSlideOut = useCallback(() => {
    delegatedProps.onOpenChange?.(false);
  }, [delegatedProps]);

  const handleClose = useCallback(() => {
    if (!overlayRef.current || !panelRef.current) {
      return;
    }

    overlayRef.current.addEventListener('transitionend', closeSlideOut);

    // Animate the overlay and panel out of view as they close.
    overlayRef.current.classList.add(OVERLAY_CLASSES.hidden);
    overlayRef.current.classList.remove(OVERLAY_CLASSES.visible);
    panelRef.current.style.transform = `translateX(${panelWidth})`;
    panelRef.current.classList.remove(PANEL_CLASSES.visible);

    return () => {
      overlayRef.current?.removeEventListener('transitionend', closeSlideOut);
    };
  }, [overlayRef, panelRef, panelWidth, closeSlideOut]);

  if (!delegatedProps.open) {
    return null;
  }

  // Attaches additional props to the Panel component that is passed in.
  const panelContent = cloneElement(children, {
    onClose: handleClose,
    ref: panelRef,
  });

  return (
    <ModalRoot {...{ ...delegatedProps, modal: true }}>
      <Portal>
        <div className="fixed inset-0 z-30" role="dialog" aria-modal="true">
          <SlideOutOverlay {...{ overlayRef, onClose: handleClose }} />
          <SlideOutPanel
            {...{
              panelRef,
              panelWidth,
              className: panelClassName,
              onClose: handleClose,
            }}
          >
            {panelContent}
          </SlideOutPanel>
        </div>
      </Portal>
    </ModalRoot>
  );
}

SlideOut.displayName = 'SlideOut';
